import type { ReactElement } from "react"
import { Link, useRouter, useRouterState } from "@tanstack/react-router"

import { FloorPilotLogo } from "@/components/brand"
import { Button } from "@/components/ui/button"
import { isPublicPathname } from "@/lib/route-error-utils"

/**
 * Fallback for non-transient route errors (render bugs, unexpected API shapes). Theme TOKENS
 * only — a root-match error renders outside ThemeProvider, where the :root CSS variables still
 * resolve but `useTheme()` would not.
 */
export function GenericErrorScreen({ error }: { error: unknown }): ReactElement {
	const router = useRouter()
	const pathname = useRouterState({ select: (s) => s.location.pathname })

	return (
		<div className="bg-background flex min-h-svh flex-col items-center justify-center gap-4 px-6 py-16">
			<FloorPilotLogo variant="navbar-wordmark" className="block h-7 w-auto" />
			<p className="text-foreground text-center text-base font-medium">Something went wrong.</p>
			<p className="text-muted-foreground max-w-md text-center text-sm">
				The error has been reported. Try again, or come back in a moment.
			</p>
			<div className="flex items-center gap-2">
				<Button
					type="button"
					variant="outline"
					onClick={() => {
						// invalidate() re-runs beforeLoad/loaders and clears errored matches; the boundary's
						// own reset() does NOT re-run them (TanStack Router #2539).
						void router.invalidate().catch(() => {})
					}}>
					Retry
				</Button>
				{/* Anonymous surfaces (share links, auth pages) shouldn't offer a dashboard bounce-to-sign-in. */}
				{!isPublicPathname(pathname) ? (
					<Button asChild>
						<Link to="/dashboard">Go to Dashboard</Link>
					</Button>
				) : null}
			</div>
			{import.meta.env.DEV && error instanceof Error ? (
				<pre className="text-muted-foreground max-w-xl overflow-x-auto text-left text-xs">{error.message}</pre>
			) : null}
		</div>
	)
}
